/**
 * Cria cards de Tempo de Corte para lotes do cadastro que ainda não têm card,
 * copiando as peças (itens) de outro card do mesmo produto (nome exato).
 */

import { parLoteProdutoCardExcluido } from './cardsTempoCorteExcluidos';

export interface LoteCadastroAutoCard {
  id: string;
  lote: string;
  produtoNome: string;
  qtd: number;
}

export interface PontuacaoProdutoAutoCard {
  nome: string;
  m2: number;
}

export interface TempoCorteItemAuto {
  id: string;
  numero: number;
  descricao: string;
  qtd: number;
  medida: string;
  metrosQuadrados: number;
  material: string;
  selecionado: boolean;
  produtoOrigem?: string;
}

export interface ProdutoTempoCorteSalvoAuto {
  id: string;
  nome: string;
  lote?: string;
  qtdProduto?: number;
  pontuacaoProduto?: number;
  pontosDiaTodo?: number;
  tempoDiaTodo?: number;
  porcentagem?: number;
  itens?: TempoCorteItemAuto[];
  tempoExato?: number;
  tempoExatoPlano?: number;
  tempoProducaoMinutos?: number;
  eUnificado?: boolean;
  dataCriacaoISO?: string;
  dataCriacao?: string;
  autoGeradoDeCardId?: string;
  [key: string]: unknown;
}

const norm = (s: string) =>
  String(s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .trim();

/** Nome completo do produto — NÃO remove sufixo numérico (1.8 ≠ 2.18 ≠ 2.8).
 *  Normaliza zeros à direita: 1.80 → 1.8, 2.20 → 2.2 (igual ao Boletim/Excel). */
export function chaveNomeProdutoTempoCorte(nome: string): string {
  return norm(nome)
    .replace(/\bBUFFET\s+ARON\s+4\s+PORTAS\b/g, 'BUFFET ARON 2.0')
    .replace(/\bMDF\b/g, '')
    .replace(/\bAGLOMERADO\b/g, '')
    .replace(/\bAGLO\b/g, '')
    .replace(/(\d),(\d)/g, '$1.$2')
    .replace(/\b(\d+)\.(\d*?)0+\b/g, (_m, intPart: string, decPart: string) => {
      const trimmed = decPart.replace(/0+$/, '');
      return trimmed ? `${intPart}.${trimmed}` : intPart;
    })
    .replace(/\s+/g, ' ')
    .trim();
}

function extrairVariantesNomeComposto(nome: string): string[] {
  const base = chaveNomeProdutoTempoCorte(nome);
  if (!base) return [];
  const partes = base
    .split(/(?:\s*\/\s*|\s+E\s+|\s*,\s*|\s*;\s*|\s*\+\s*|\s*&\s*)/i)
    .map((p) => p.trim())
    .filter(Boolean);
  if (partes.length <= 1) return [base];
  const variantes = new Set<string>([base, partes[0]]);
  const tokensPrimeira = partes[0].split(/\s+/).filter(Boolean);
  const prefixo = tokensPrimeira.length > 1 ? tokensPrimeira.slice(0, -1).join(' ') : '';
  for (let i = 1; i < partes.length; i++) {
    const p = partes[i];
    const tokens = p.split(/\s+/).filter(Boolean);
    if (tokens.length >= 2 || (prefixo && p.startsWith(prefixo))) variantes.add(p);
    else if (prefixo) variantes.add(`${prefixo} ${p}`.trim());
    else variantes.add(p);
  }
  return Array.from(variantes);
}

/** Igualdade: preserva MDF/AGLO como diferença (NICHO ≠ MDF NICHO ≠ MDF TAMPO NICHO). */
export function nomesProdutoIguais(nomeA: string, nomeB: string): boolean {
  const normComMaterial = (nome: string) =>
    norm(nome)
      .replace(/\bBUFFET\s+ARON\s+4\s+PORTAS\b/g, 'BUFFET ARON 2.0')
      .replace(/(\d),(\d)/g, '$1.$2')
      .replace(/\b(\d+)\.(\d*?)0+\b/g, (_m, intPart: string, decPart: string) => {
        const trimmed = decPart.replace(/0+$/, '');
        return trimmed ? `${intPart}.${trimmed}` : intPart;
      })
      .replace(/\s+/g, ' ')
      .trim();
  return normComMaterial(nomeA) === normComMaterial(nomeB);
}

/** Só para nomes compostos (ex.: BUFFET ANDORRA/SIERRA). */
function nomesProdutoVarianteComposta(nomeA: string, nomeB: string): boolean {
  if (nomesProdutoIguais(nomeA, nomeB)) return true;
  const varsA = extrairVariantesNomeComposto(nomeA);
  const varsB = extrairVariantesNomeComposto(nomeB);
  return varsA.some((va) => varsB.some((vb) => va === vb));
}

export function cardExisteParaLoteProduto(
  cards: ProdutoTempoCorteSalvoAuto[],
  produtoNome: string,
  lote: string
): boolean {
  const loteN = norm(lote);
  return cards.some(
    (c) => norm(String(c.lote || '')) === loteN && nomesProdutoIguais(String(c.nome || ''), produtoNome)
  );
}

function parseNumeroFlex(valor: unknown): number {
  if (typeof valor === 'number') return Number.isFinite(valor) ? valor : 0;
  const texto = String(valor ?? '').trim();
  if (!texto) return 0;
  const normalizado = texto.replace(/\s+/g, '').replace(/\.(?=\d{3}(?:\D|$))/g, '').replace(',', '.');
  const n = Number(normalizado);
  return Number.isFinite(n) ? n : 0;
}

function calcularMetrosQuadradosPorMedidaQtd(medida: string, qtd: number): number {
  const partes = String(medida ?? '')
    .toLowerCase()
    .split(/[x×*]/)
    .map((p) => parseNumeroFlex(p))
    .filter((n) => n > 0);
  if (partes.length < 2 || qtd <= 0) return 0;
  return partes.reduce((acc, n) => acc * n, 1) * qtd;
}

function recalcularMetrosQuadradosItens(itens: TempoCorteItemAuto[]): TempoCorteItemAuto[] {
  return (Array.isArray(itens) ? itens : []).map((item, index) => {
    const qtd = parseNumeroFlex(item?.qtd);
    const medida = String(item?.medida ?? '').trim();
    const m2Calculado = calcularMetrosQuadradosPorMedidaQtd(medida, qtd);
    const m2Atual = parseNumeroFlex(item?.metrosQuadrados);
    return {
      ...item,
      numero: index + 1,
      qtd,
      medida,
      metrosQuadrados: m2Calculado > 0 ? m2Calculado : m2Atual,
    };
  });
}

function calcularTempoExatoPlano(
  pontuacaoProduto: number,
  qtdProduto: number,
  pontosDiaTodo: number,
  tempoDiaTodo: number
): number {
  const pontuacaoRemessa = pontuacaoProduto > 0 ? pontosDiaTodo / pontuacaoProduto : 0;
  const x = tempoDiaTodo > 0 ? pontuacaoRemessa / tempoDiaTodo : 0;
  return x > 0 ? qtdProduto / x : 0;
}

function calcularTempoExatoSelecionadas(produto: {
  pontuacaoProduto: number;
  qtdProduto: number;
  pontosDiaTodo: number;
  tempoDiaTodo: number;
  porcentagem: number;
  itens: TempoCorteItemAuto[];
}): number {
  const itens = recalcularMetrosQuadradosItens(produto.itens);
  const totalM2 = itens.reduce((acc, item) => acc + (item.metrosQuadrados || 0), 0);
  const tempoExato = calcularTempoExatoPlano(
    produto.pontuacaoProduto,
    produto.qtdProduto,
    produto.pontosDiaTodo,
    produto.tempoDiaTodo
  );
  const soma = itens
    .filter((i) => i.selecionado !== false)
    .reduce((acc, item) => {
      const m2 = item.metrosQuadrados || 0;
      const percPeca =
        totalM2 > 0 && produto.porcentagem > 0 ? (m2 * produto.porcentagem) / totalM2 : 0;
      const tempoPeca =
        produto.porcentagem > 0 ? (tempoExato * percPeca) / produto.porcentagem : 0;
      return acc + tempoPeca;
    }, 0);
  return soma > 0 ? soma : tempoExato;
}

function novoId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function clonarItensDeTemplate(itens: TempoCorteItemAuto[]): TempoCorteItemAuto[] {
  return recalcularMetrosQuadradosItens(
    (Array.isArray(itens) ? itens : []).map((it) => ({
      ...it,
      id: novoId(),
      selecionado: it.selecionado !== false,
    }))
  );
}

function dataMsCard(c: ProdutoTempoCorteSalvoAuto): number {
  return (
    Date.parse(String(c.dataCriacaoISO || '')) ||
    Date.parse(String(c.salvoEm || '')) ||
    0
  );
}

function ordenarTemplates(candidatos: ProdutoTempoCorteSalvoAuto[]): ProdutoTempoCorteSalvoAuto {
  const sorted = [...candidatos].sort((a, b) => {
    const uniA = a.eUnificado ? 1 : 0;
    const uniB = b.eUnificado ? 1 : 0;
    if (uniA !== uniB) return uniA - uniB;
    return dataMsCard(b) - dataMsCard(a);
  });
  return sorted[0];
}

/** Busca card modelo com o mesmo nome exato do produto (outro lote). */
export function encontrarTemplateCardTempoCorte(
  cards: ProdutoTempoCorteSalvoAuto[],
  produtoNome: string,
  loteDestino: string
): ProdutoTempoCorteSalvoAuto | null {
  const loteDest = norm(loteDestino);
  const comItens = cards.filter((c) => (Array.isArray(c.itens) ? c.itens.length : 0) > 0);

  const exatosOutroLote = comItens.filter(
    (c) =>
      norm(String(c.lote || '')) !== loteDest &&
      nomesProdutoIguais(String(c.nome || ''), produtoNome)
  );
  if (exatosOutroLote.length > 0) return ordenarTemplates(exatosOutroLote);

  const compostosOutroLote = comItens.filter(
    (c) =>
      norm(String(c.lote || '')) !== loteDest &&
      !nomesProdutoIguais(String(c.nome || ''), produtoNome) &&
      nomesProdutoVarianteComposta(String(c.nome || ''), produtoNome)
  );
  if (compostosOutroLote.length > 0) return ordenarTemplates(compostosOutroLote);

  return null;
}

/** Mesma lógica do botão "Buscar pontuação no Boletim pelo lote". */
function pontuacaoDeCadastro(
  produtoNome: string,
  pontuacoes: PontuacaoProdutoAutoCard[]
): number | null {
  const alvo = chaveNomeProdutoTempoCorte(produtoNome);
  if (!alvo || !Array.isArray(pontuacoes) || pontuacoes.length === 0) return null;
  const lista = pontuacoes.map((p) => ({
    m2: Number(p.m2) || 0,
    nomeNorm: chaveNomeProdutoTempoCorte(p.nome),
  }));
  let achou = lista.find((p) => p.nomeNorm === alvo);
  if (!achou) achou = lista.find((p) => p.nomeNorm.includes(alvo));
  if (!achou) achou = lista.find((p) => alvo.includes(p.nomeNorm));
  return achou && achou.m2 > 0 ? achou.m2 : null;
}

export function clonarCardTempoCorteParaLote(
  template: ProdutoTempoCorteSalvoAuto,
  loteCadastro: LoteCadastroAutoCard,
  pontuacoes: PontuacaoProdutoAutoCard[] = []
): ProdutoTempoCorteSalvoAuto {
  const qtdProduto = Math.max(1, Number(loteCadastro.qtd) || Number(template.qtdProduto) || 1);
  const pontuacaoProduto =
    pontuacaoDeCadastro(loteCadastro.produtoNome, pontuacoes) ??
    (Number(template.pontuacaoProduto) || 0);
  const pontosDiaTodo = Number(template.pontosDiaTodo) || 0;
  const tempoDiaTodo = Number(template.tempoDiaTodo) || 0;
  const porcentagem = Number(template.porcentagem) || 0;
  const itens = clonarItensDeTemplate(template.itens || []);
  const baseCalc = {
    pontuacaoProduto,
    qtdProduto,
    pontosDiaTodo,
    tempoDiaTodo,
    porcentagem,
    itens,
  };
  const tempoExatoPlano = calcularTempoExatoPlano(
    pontuacaoProduto,
    qtdProduto,
    pontosDiaTodo,
    tempoDiaTodo
  );
  const tempoExato = calcularTempoExatoSelecionadas(baseCalc);
  const agora = new Date();
  return {
    id: novoId(),
    nome: String(loteCadastro.produtoNome || template.nome || '').trim().toUpperCase(),
    lote: String(loteCadastro.lote || '').trim(),
    pontuacaoProduto,
    qtdProduto,
    pontosDiaTodo,
    tempoDiaTodo,
    porcentagem,
    itens,
    tempoExato,
    tempoExatoPlano: tempoExatoPlano > 0 ? tempoExatoPlano : tempoExato,
    tempoProducaoMinutos: 0,
    dataHoraInicioExato: '',
    dataHoraTerminoExato: '',
    dataHoraInicioProducao: '',
    dataHoraTerminoProducao: '',
    horarioExtraManha: Boolean(template.horarioExtraManha),
    horarioExtraAlmoco: Boolean(template.horarioExtraAlmoco),
    trabalhaSabado: Boolean(template.trabalhaSabado),
    trabalhaDomingo: false,
    compartilharTempoProducao: template.compartilharTempoProducao !== false,
    horarioProduto: '',
    enviarParaAnalise: false,
    dataCriacao: agora.toLocaleDateString('pt-BR'),
    dataCriacaoISO: agora.toISOString(),
    autoGeradoDeCardId: String(template.id || ''),
  };
}

export type MotivoPendenciaCardLote = 'sem_template' | 'pronto_para_criar';

export interface PendenciaCardLote {
  lote: string;
  produtoNome: string;
  qtd: number;
  motivo: MotivoPendenciaCardLote;
  /** Card de outro lote que será usado como modelo (se houver). */
  templateNome?: string;
  templateLote?: string;
}

/** Lista tudo que está no cadastro de Lotes mas ainda não tem card correspondente no Tempo de Corte. */
export function listarPendenciasCardsTempoCorte(
  lotesCadastro: LoteCadastroAutoCard[],
  cardsAtuais: ProdutoTempoCorteSalvoAuto[]
): PendenciaCardLote[] {
  const pendencias: PendenciaCardLote[] = [];
  const vistos = new Set<string>();

  for (const lp of lotesCadastro) {
    const lote = String(lp.lote || '').trim();
    const produtoNome = String(lp.produtoNome || '').trim();
    if (!lote || !produtoNome) continue;
    // Chave com material (MDF/AGLO) — NICHO e MDF TAMPO NICHO são pendências distintas.
    const chave = `${norm(lote)}|${norm(produtoNome)
      .replace(/(\d),(\d)/g, '$1.$2')
      .replace(/\b(\d+)\.(\d*?)0+\b/g, (_m, intPart: string, decPart: string) => {
        const trimmed = decPart.replace(/0+$/, '');
        return trimmed ? `${intPart}.${trimmed}` : intPart;
      })
      .replace(/\s+/g, ' ')
      .trim()}`;
    if (vistos.has(chave)) continue;
    vistos.add(chave);

    if (cardExisteParaLoteProduto(cardsAtuais, produtoNome, lote)) continue;
    // Usuário apagou o card deste lote+produto — não listar como pendência nem recriar.
    if (parLoteProdutoCardExcluido(lote, produtoNome)) continue;

    const template = encontrarTemplateCardTempoCorte(cardsAtuais, produtoNome, lote);
    pendencias.push({
      lote,
      produtoNome,
      qtd: Number(lp.qtd) || 0,
      motivo: template ? 'pronto_para_criar' : 'sem_template',
      templateNome: template ? String(template.nome || '') : undefined,
      templateLote: template ? String(template.lote || '') : undefined,
    });
  }

  pendencias.sort((a, b) => {
    const la = norm(a.lote);
    const lb = norm(b.lote);
    if (la !== lb) return la.localeCompare(lb, undefined, { numeric: true });
    return chaveNomeProdutoTempoCorte(a.produtoNome).localeCompare(
      chaveNomeProdutoTempoCorte(b.produtoNome)
    );
  });
  return pendencias;
}

export interface LoteResumoPendenciasCards {
  lote: string;
  cadastro: { produtoNome: string; qtd: number; temCard: boolean }[];
  faltam: PendenciaCardLote[];
}

/** Agrupa cadastro × cards por lote para exibir na UI. */
export function resumirPendenciasPorLote(
  lotesCadastro: LoteCadastroAutoCard[],
  cardsAtuais: ProdutoTempoCorteSalvoAuto[]
): LoteResumoPendenciasCards[] {
  const pendencias = listarPendenciasCardsTempoCorte(lotesCadastro, cardsAtuais);
  const porLote = new Map<string, LoteCadastroAutoCard[]>();
  const vistos = new Set<string>();

  for (const lp of lotesCadastro) {
    const lote = String(lp.lote || '').trim();
    const produtoNome = String(lp.produtoNome || '').trim();
    if (!lote || !produtoNome) continue;
    const chave = `${norm(lote)}|${chaveNomeProdutoTempoCorte(produtoNome)}`;
    if (vistos.has(chave)) continue;
    vistos.add(chave);
    if (!porLote.has(lote)) porLote.set(lote, []);
    porLote.get(lote)!.push(lp);
  }

  const faltamPorLote = new Map<string, PendenciaCardLote[]>();
  for (const p of pendencias) {
    if (!faltamPorLote.has(p.lote)) faltamPorLote.set(p.lote, []);
    faltamPorLote.get(p.lote)!.push(p);
  }

  return [...porLote.entries()]
    .map(([lote, itens]) => ({
      lote,
      cadastro: itens.map((lp) => ({
        produtoNome: lp.produtoNome,
        qtd: Number(lp.qtd) || 0,
        temCard: cardExisteParaLoteProduto(cardsAtuais, lp.produtoNome, lote),
      })),
      faltam: faltamPorLote.get(lote) ?? [],
    }))
    .filter((r) => r.faltam.length > 0)
    .sort((a, b) =>
      norm(a.lote).localeCompare(norm(b.lote), undefined, { numeric: true })
    );
}

export interface ResultadoAutoCardsTempoCorte {
  cards: ProdutoTempoCorteSalvoAuto[];
  novos: ProdutoTempoCorteSalvoAuto[];
  ignoradosSemTemplate: { lote: string; produtoNome: string }[];
}

/** Gera cards faltantes a partir do cadastro de lotes + templates existentes.
 *  Nunca recria o que o usuário apagou (lista de exclusão). */
export function gerarCardsTempoCorteFaltantes(
  lotesCadastro: LoteCadastroAutoCard[],
  cardsAtuais: ProdutoTempoCorteSalvoAuto[],
  pontuacoes: PontuacaoProdutoAutoCard[] = []
): ResultadoAutoCardsTempoCorte {
  const cards = [...cardsAtuais];
  const novos: ProdutoTempoCorteSalvoAuto[] = [];
  const ignoradosSemTemplate: { lote: string; produtoNome: string }[] = [];

  const pendencias = listarPendenciasCardsTempoCorte(lotesCadastro, cardsAtuais);

  for (const p of pendencias) {
    if (p.motivo !== 'pronto_para_criar') {
      ignoradosSemTemplate.push({ lote: p.lote, produtoNome: p.produtoNome });
      continue;
    }
    // Dupla checagem: exclusão manual tem prioridade absoluta sobre o cadastro de Lotes.
    if (parLoteProdutoCardExcluido(p.lote, p.produtoNome)) continue;
    // Já criado neste mesmo lote nesta passagem (ou estava em cardsAtuais).
    if (cardExisteParaLoteProduto(cards, p.produtoNome, p.lote)) continue;

    const lp = lotesCadastro.find(
      (x) =>
        norm(x.lote) === norm(p.lote) && nomesProdutoIguais(x.produtoNome, p.produtoNome)
    );
    if (!lp) continue;

    const template = encontrarTemplateCardTempoCorte(cards, p.produtoNome, p.lote);
    if (!template) {
      ignoradosSemTemplate.push({ lote: p.lote, produtoNome: p.produtoNome });
      continue;
    }

    const card = clonarCardTempoCorteParaLote(template, lp, pontuacoes);
    if (parLoteProdutoCardExcluido(String(card.lote || ''), String(card.nome || ''))) continue;
    cards.push(card);
    novos.push(card);
  }

  return { cards, novos, ignoradosSemTemplate };
}

export function resumirPendentesAutoCards(
  lotesCadastro: LoteCadastroAutoCard[],
  cardsAtuais: ProdutoTempoCorteSalvoAuto[]
): { faltam: number; comTemplate: number; semTemplate: number } {
  const pendencias = listarPendenciasCardsTempoCorte(lotesCadastro, cardsAtuais);
  const comTemplate = pendencias.filter((p) => p.motivo === 'pronto_para_criar').length;
  const semTemplate = pendencias.filter((p) => p.motivo === 'sem_template').length;
  return { faltam: pendencias.length, comTemplate, semTemplate };
}
